import React, {useEffect, useState} from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {useSession} from '../../../../../../firebase/UserProvider';

const PRIORITIES = ['Low', 'Routine', 'Medium', 'High', 'Critical'];
const _awpBase = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';
function _authH() { const t = localStorage.getItem('access_token'); return t ? {Authorization: `Bearer ${t}`, 'Content-Type': 'application/json'} : {'Content-Type': 'application/json'}; }

// Generate a unique requestId by listing existing IR documents.
async function ensureUniqueRequestId(prefix) {
    const res = await fetch(`${_awpBase}/api/intelligence-requests?type=IR&limit=1000`, {headers: _authH()});
    const docs = res.ok ? await res.json() : [];
    const existing = new Set((Array.isArray(docs) ? docs : []).map(d => d.id));
    let seq = 1;
    while (true) {
        const candidate = `${prefix}-${String(seq).padStart(3, '0')}`;
        if (!existing.has(candidate)) return candidate;
        seq += 1;
    }
}

export default function CreateIRDialog({open, onClose}) {
    const {user} = useSession() || {user: null};
    const [requestId, setRequestId] = useState('');
    const [title, setTitle] = useState('Iran Response to Tier-2 Sanctions');
    const [fullQuestion, setFullQuestion] = useState('What are Iran’s likely political, military, cyber, and proxy responses if the U.S. implements Tier-2 sanctions before September?\n' +
        'How might Iran leverage regional or international diplomatic channels to counteract Tier-2 sanctions?\n' +
        'Which domestic political factions in Iran are likely to advocate for aggressive vs. conciliatory responses?\n' +
        'Could Iran seek to strengthen ties with countries outside the West (China, Russia, Turkey) as a response to Tier-2 sanctions?');
    const [priority, setPriority] = useState('High');
    const [purpose, setPurpose] = useState('Inform sanctions decision');
    const [timeframe, setTimeframe] = useState('20 days');
    const [stakeholders, setStakeholders] = useState('NSC, State Dept, Treasury, DoD');
    const [dependencies, setDependencies] = useState('Prior Iran military and economic assessments');
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        let mounted = true;
        (async () => {
            setError(null);
            try {
                const year = new Date().getFullYear();
                const prefix = `IR-${year}`;
                const id = await ensureUniqueRequestId(prefix);
                if (!mounted) return;
                setRequestId(id);
            } catch (e) {
                console.error('Error generating requestId:', e);
                if (!mounted) return;
                setError('Could not generate request ID. Please try again.');
            }
        })();
        return () => {
            mounted = false;
        };
    }, [open]);

    const handleClose = () => {
        if (saving) return;
        setError(null);
        setRequestId('');
        onClose?.();
    };

    const handleCreate = async () => {
        if (!user?.uid) {
            setError('You must be signed in to create an intelligence request.');
            return;
        }
        if (!fullQuestion) {
            setError('Please provide the full IR question.');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            let finalRequestId = requestId;
            if (!finalRequestId) {
                const year = new Date().getFullYear();
                const prefix = `IR-${year}`;
                finalRequestId = await ensureUniqueRequestId(prefix);
            }

            const payload = {
                requestId: finalRequestId,
                title: title.trim(),
                fullQuestion: fullQuestion.trim(),
                priority,
                purpose: purpose.trim(),
                timeframe: timeframe.trim(),
                stakeholders: stakeholders.split(',').map(s => s.trim()).filter(Boolean),
                dependencies: dependencies.trim(),
                createdAt: new Date().toISOString(),
                createdBy: user.uid,
                status: 'open',
            };

            await fetch(`${_awpBase}/api/intelligence-requests`, {method: 'POST', headers: _authH(), body: JSON.stringify({...payload, type: 'IR', id: finalRequestId})});
            setSaving(false);
            setRequestId('');
            onClose?.();
        } catch (e) {
            console.error('Error creating intelligence request:', e);
            setError(e?.message || String(e));
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle>
                <Stack direction={'row'} justifyContent="space-between" alignItems="center">
                    <Stack>
                        Create Intelligence Requirements
                    </Stack>
                    <Typography variant="subtitle1">ID: {requestId ? requestId : 'Generating ID...'}</Typography>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <TextField
                    label="Title"
                    fullWidth
                    margin="normal"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                />
                <TextField
                    autoFocus={true}
                    label="Key Intelligence Questions (KIQs)"
                    fullWidth
                    margin="normal"
                    multiline
                    minRows={3}
                    sx={{whiteSpace: 'pre-line'}}
                    value={fullQuestion}
                    onChange={(e) => setFullQuestion(e.target.value)}
                />
                <TextField
                    label="Purpose"
                    fullWidth
                    margin="normal"
                    value={purpose}
                    onChange={(e) => setPurpose(e.target.value)}
                />
                <TextField
                    label="Dependencies"
                    fullWidth
                    margin="normal"
                    value={dependencies}
                    onChange={(e) => setDependencies(e.target.value)}
                />
                <Stack direction={'row'} mt={2} spacing={2}>
                    <FormControl fullWidth margin="normal">
                        <InputLabel id="ir-priority-label">Priority</InputLabel>
                        <Select
                            labelId="ir-priority-label"
                            value={priority}
                            label="Priority"
                            variant="outlined"
                            onChange={(e) => setPriority(e.target.value)}
                        >
                            {PRIORITIES.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Timeframe"
                        fullWidth
                        margin="normal"
                        value={timeframe}
                        onChange={(e) => setTimeframe(e.target.value)}
                    />
                    <TextField
                        label="Stakeholders (comma separated)"
                        fullWidth
                        margin="normal"
                        value={stakeholders}
                        onChange={(e) => setStakeholders(e.target.value)}
                    />
                </Stack>
                {error &&
                    <Typography color={'error'} variant={'body2'} style={{marginTop: 8}}>{String(error)}</Typography>}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving}>Cancel</Button>
                <Button onClick={handleCreate} variant="contained"
                        disabled={saving}>{saving ? 'Creating…' : 'Create Request'}</Button>
            </DialogActions>
        </Dialog>
    );
}
